import { Navigate, useLocation } from '@tanstack/react-router'
import { HugeiconsIcon } from '@hugeicons/react'
import { Shield01Icon } from '@hugeicons/core-free-icons'
import { useConvexAuth } from 'convex/react'
import { useAuthState } from '../hooks/useAuthState'
import { usePermissions } from '../hooks/usePermissions'
import { useIsPlatformAdmin } from '../hooks/useIsPlatformAdmin'
import { FullPageSpinner } from '@/components/common/full-page-spinner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

interface AuthGuardProps {
  children: React.ReactNode
  fallback?: React.ReactNode
  requiredRole?: string
  requiresPlatformAdmin?: boolean
  requiredOrganizationRole?: {
    organizationId: string
    role: string | Array<string>
  }
}

export function AuthGuard({
  children,
  fallback,
  requiredRole,
  requiresPlatformAdmin,
  requiredOrganizationRole,
}: AuthGuardProps) {
  const { isAuthenticated, isLoading } = useAuthState()
  const { hasRole, hasOrganizationRole } = usePermissions()
  const { isLoading: isConvexLoading } = useConvexAuth()
  const isPlatformAdmin = useIsPlatformAdmin()
  const location = useLocation()

  // Loading state
  if (isLoading) {
    return fallback || <FullPageSpinner />
  }

  // Not authenticated — redirect to sign-in, preserving current location
  if (!isAuthenticated) {
    return <Navigate to="/sign-in" search={{ redirectTo: location.pathname }} />
  }

  // Platform admin check — wait for Convex auth to resolve before denying
  if (requiresPlatformAdmin) {
    if (isConvexLoading) {
      return fallback || <FullPageSpinner />
    }
    if (!isPlatformAdmin) {
      return (
        <div className="flex items-center justify-center min-h-[400px] p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                <HugeiconsIcon
                  icon={Shield01Icon}
                  className="h-6 w-6 text-destructive"
                />
              </div>
              <CardTitle className="text-xl">Access Denied</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-muted-foreground mb-4">
                You don't have the required permissions to access this page.
              </p>
              <p className="text-sm text-muted-foreground">
                Required role:{' '}
                <span className="font-medium text-foreground">
                  Platform Administrator
                </span>
              </p>
            </CardContent>
          </Card>
        </div>
      )
    }
  }

  // Check role requirements
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <HugeiconsIcon
                icon={Shield01Icon}
                className="h-6 w-6 text-destructive"
              />
            </div>
            <CardTitle className="text-xl">Access Denied</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted-foreground mb-4">
              You don't have the required permissions to access this page.
            </p>
            <p className="text-sm text-muted-foreground">
              Required role:{' '}
              <span className="font-medium text-foreground">
                {requiredRole}
              </span>
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Check organization role requirements
  if (requiredOrganizationRole) {
    const requiredRoles = Array.isArray(requiredOrganizationRole.role)
      ? requiredOrganizationRole.role
      : [requiredOrganizationRole.role]

    const hasRequiredRole = requiredRoles.some((role) =>
      hasOrganizationRole(requiredOrganizationRole.organizationId, role),
    )

    if (!hasRequiredRole) {
      return (
        <div className="flex items-center justify-center min-h-[400px] p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                <HugeiconsIcon
                  icon={Shield01Icon}
                  className="h-6 w-6 text-destructive"
                />
              </div>
              <CardTitle className="text-xl">Access Denied</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-muted-foreground mb-4">
                You don't have the required permissions to access this page.
              </p>
              <p className="text-sm text-muted-foreground">
                Required role:{' '}
                <span className="font-medium text-foreground">
                  {requiredRoles.join(' or ')}
                </span>
              </p>
            </CardContent>
          </Card>
        </div>
      )
    }
  }

  return <>{children}</>
}
